import type { ApiClient } from '@/helpers/apiClient'
import {
  billingCardSetupDtoSchema,
  billingHistoryDtoSchema,
  billingPurchaseDtoSchema,
  billingRefundDtoSchema,
  billingSummaryDtoSchema,
  type BillingCardSetupDto,
  type BillingHistoryDto,
  type BillingPurchaseDto,
  type BillingRefundDto,
  type BillingSummaryDto,
} from '@lib/dto/billing.dto'
import type {
  CreateCardSetupInput,
  CreatePurchaseInput,
  CreateRefundInput,
  SetAutoReloadInput,
  SetLowBalanceThresholdInput,
} from '@lib/input/billing.input'
import type { Uuid } from '@lib/validators/params'
import type { IBillingService } from '@/types/BillingService.types'

function base(tenantId: Uuid): string {
  return `/dashboard/tenants/${encodeURIComponent(tenantId)}/billing`
}

function purchasePath(tenantId: Uuid, purchaseId: Uuid): string {
  return `${base(tenantId)}/purchases/${encodeURIComponent(purchaseId)}`
}

/** An organization's prepaid credit, bound to a dashboard session's client (the tenant's, or staff view-as). */
export class BillingService implements IBillingService {
  constructor(private readonly client: ApiClient) {}

  summary(tenantId: Uuid): Promise<BillingSummaryDto> {
    return this.client.get(base(tenantId), billingSummaryDtoSchema)
  }

  history(tenantId: Uuid, before: string | null): Promise<BillingHistoryDto> {
    const search = before ? `?${new URLSearchParams({ before }).toString()}` : ''
    return this.client.get(`${base(tenantId)}/history${search}`, billingHistoryDtoSchema)
  }

  createPurchase(tenantId: Uuid, input: CreatePurchaseInput): Promise<BillingPurchaseDto> {
    return this.client.post(`${base(tenantId)}/purchases`, input, billingPurchaseDtoSchema)
  }

  purchase(tenantId: Uuid, purchaseId: Uuid): Promise<BillingPurchaseDto> {
    return this.client.get(purchasePath(tenantId, purchaseId), billingPurchaseDtoSchema)
  }

  cancelPurchase(tenantId: Uuid, purchaseId: Uuid): Promise<BillingPurchaseDto> {
    return this.client.post(`${purchasePath(tenantId, purchaseId)}/cancel`, {}, billingPurchaseDtoSchema)
  }

  createRefund(tenantId: Uuid, input: CreateRefundInput): Promise<BillingRefundDto> {
    return this.client.post(`${base(tenantId)}/refunds`, input, billingRefundDtoSchema)
  }

  setLowBalanceThreshold(tenantId: Uuid, input: SetLowBalanceThresholdInput): Promise<BillingSummaryDto> {
    return this.client.patch(`${base(tenantId)}/threshold`, input, billingSummaryDtoSchema)
  }

  setAutoReload(tenantId: Uuid, input: SetAutoReloadInput): Promise<BillingSummaryDto> {
    return this.client.put(`${base(tenantId)}/auto-reload`, input, billingSummaryDtoSchema)
  }

  createCardSetup(tenantId: Uuid, input: CreateCardSetupInput): Promise<BillingCardSetupDto> {
    return this.client.post(`${base(tenantId)}/card-setups`, input, billingCardSetupDtoSchema)
  }

  cardSetup(tenantId: Uuid, setupId: Uuid): Promise<BillingCardSetupDto> {
    return this.client.get(`${base(tenantId)}/card-setups/${encodeURIComponent(setupId)}`, billingCardSetupDtoSchema)
  }

  cancelCardSetup(tenantId: Uuid, setupId: Uuid): Promise<BillingCardSetupDto> {
    return this.client.post(`${base(tenantId)}/card-setups/${encodeURIComponent(setupId)}/cancel`, {}, billingCardSetupDtoSchema)
  }
}
